import { css } from "@emotion/css"
import styled from "@emotion/styled"
import { faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  IconButton,
  InputBase,
  ListItem,
  Paper,
  TablePagination,
  TextField,
  Tooltip,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import frLocale from "date-fns/locale/fr"
import { format } from "date-fns"
import { Fragment, useEffect, useState } from "react"
import colors from "../../../style/colors"
import GlobalBgStyle from "../../../style/GlobalBgStyle"
import {
  formatStrToDate,
  getFirstDayOfCurrentYear,
  getLastDayOfCurrentYear,
  getMessageFromType,
} from "../../../utils/utils"
import Loader from "../../../components/Loader"
import InternError from "../../../components/InternError"
import { useNavigate } from "react-router-dom"
import PartnerRequest, {
  getStatusTypeLabel,
  StatusType,
} from "../../../models/partnerRequest"
import {
  useDeletePartnerRequestMutation,
  useGetPartnerRequestsWithPaginationQuery,
} from "../../../features/partnerRequestSlice"
import ValidateDialog from "../../../components/ValidateDialog"
import { toast } from "react-toastify"
import { ResponseApiException } from "../../../models/responseApiType"

const StyledDiv = styled.div`
  p {
    margin: 0;
    padding: 0;
  }
  h1 {
    font-size: 1.4rem;
    color: ${colors.blue};
    text-align: center;
    margin-bottom: 2rem;
  }

  @media all and (min-width: 768px) {
    h1 {
      font-size: 1.7rem;
    }
  }
`

const AlignItemsList = ({
  partdiags,
  onItemClick,
  onRemove,
}: {
  partdiags: PartnerRequest[]
  onItemClick: (id: number) => void
  onRemove: (pubdiag: PartnerRequest) => void
}) => (
  <div className="lg:w-2/3 w-full">
    {partdiags.map((partdiag: PartnerRequest) => (
      <Fragment key={partdiag.id}>
        <div className="flex flex-row bg-white items-center">
          <ListItem
            alignItems="flex-start"
            className="justify-center flex-wrap md:flex-nowrap cursor-pointer p-0 my-2"
            onClick={() => onItemClick(partdiag.id)}
          >
            <div className="bg-white px-4 py-2 flex flex-col items-center w-full space-y-2">
              <div className="w-full flex lg:flex-row flex-col lg:space-y-0 space-y-3">
                <div className="lg:w-1/2 w-full flex flex-col items-start">
                  <p className="text-lg font-semibold">{partdiag.email}</p>
                  <p>{partdiag.number}</p>
                  <p>{partdiag.phone}</p>
                  <p>
                    Demande effetué le{" "}
                    {formatStrToDate(partdiag.createdAt, "dd/MM/yyyy")}
                  </p>
                </div>

                <div className="lg:w-1/2 w-full flex lg:justify-end justify-center items-center">
                  {" "}
                  {partdiag.status === "ToHandle" ? (
                    <p className="bg-[#777777] text-white rounded-full px-2">
                      {getStatusTypeLabel(StatusType.TO_HANDLE)}
                    </p>
                  ) : partdiag.status === "Traited" ? (
                    <p className="bg-[#007700] text-white rounded-full px-2">
                      {getStatusTypeLabel(StatusType.TRAITED)}
                    </p>
                  ) : (
                    <p className="bg-[#dfe608] rounded-full px-2">
                      {getStatusTypeLabel(StatusType.PENDING)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </ListItem>
          <div className="px-4">
            <Tooltip title="Supprimer">
              <IconButton
                onClick={() => onRemove(partdiag)}
                aria-label="supprimer"
                color="error"
              >
                <FontAwesomeIcon icon={faTrash} color="#e70202" size="xs" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </Fragment>
    ))}
  </div>
)

function DiagRequestContent() {
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [skip, setSkip] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [order, setOrder] = useState({
    orderBy: "email",
    direction: "ASC",
  })

  const [from, setFrom] = useState<Date | null>(getFirstDayOfCurrentYear())
  const [to, setTo] = useState<Date | null>(getLastDayOfCurrentYear())
  const [fromDate, setFromDate] = useState<string | null>()
  const [toDate, setToDate] = useState<string | null>()

  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [partdiagSelected, setPartdiagSelected] =
    useState<PartnerRequest | null>(null)

  const [deletePartnerRequest, { isLoading: isDeleteLoading }] =
    useDeletePartnerRequestMutation()

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(1)
  }

  const handleSort = (selectedColumn: any, sortDirection: any) => {
    setOrder({
      orderBy: selectedColumn.sortField,
      direction: sortDirection,
    })
  }

  const handleRemove = (pubdiag: PartnerRequest) => {
    setPartdiagSelected(pubdiag)
    setShowConfirmDelete(true)
  }

  const handleDelete = () => {
    if (partdiagSelected?.id) {
      deletePartnerRequest(partdiagSelected?.id)
        .unwrap()
        .then(() => {
          setShowConfirmDelete(false)
          toast.success("La demande de diagnostic a bien été supprimé")
        })
        .catch((error: ResponseApiException) => {
          let msg = getMessageFromType(error)
          toast.error(msg)
        })
    }
  }

  const {
    data: partInfosPaginate = [],
    isLoading,
    isSuccess,
    isError,
  } = useGetPartnerRequestsWithPaginationQuery(
    {
      limit: rowsPerPage,
      page: page,
      orderBy: order.orderBy,
      direction: order.direction,
      search: searchTerm,
      from: fromDate,
      to: toDate,
    },
    { skip: skip || !fromDate || !toDate }
  )

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSkip(false)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchTerm])

  useEffect(() => {
    try {
      let date1 = from ? format(from, "yyyy-MM-dd HH:mm:ss") : null
      let date2 = to ? format(to, "yyyy-MM-dd HH:mm:ss") : null
      setFromDate(date1)
      setToDate(date2)
      console.log(fromDate, toDate)
    } catch (error) {
      console.error(error)
    }
  }, [from, to])

  const navigate = useNavigate()
  let content

  if (isLoading) {
    content = <Loader />
  } else if (isError) {
    content = <InternError />
  } else if (isSuccess) {
    content =
      partInfosPaginate.items && partInfosPaginate.items.length > 0 ? (
        <AlignItemsList
          partdiags={partInfosPaginate.items}
          onItemClick={(id: number) => navigate(`/admin/diagRequest/${id}`)}
          onRemove={(partdiag: PartnerRequest) => handleRemove(partdiag)}
        />
      ) : (
        "Aucun résultat"
      )
  }

  return (
    <StyledDiv className="container">
      <GlobalBgStyle />
      <h1>Demandes de diagnostics</h1>
      <Paper
        component="form"
        sx={{
          p: "2px 4px",
          display: "flex",
          alignItems: "center",
          maxWidth: 400,
          marginBottom: "1rem",
        }}
        className="ms-auto"
        onSubmit={(e: any) => {
          e.preventDefault()
          setSkip(false)
        }}
      >
        <InputBase
          sx={{ ml: 1, flex: 1, width: "100%" }}
          placeholder="Rechercher"
          inputProps={{ "aria-label": "rechercher" }}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setSkip(true)
          }}
        />
        <IconButton
          type="submit"
          sx={{ p: "10px" }}
          aria-label="recherche"
          onClick={() => setSkip(false)}
        >
          <FontAwesomeIcon icon={faSearch} />
        </IconButton>
      </Paper>
      <div className="my-4">
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={frLocale}
        >
          <div className="d-flex flex-wrap gap-5 justify-content-center">
            <DatePicker
              label="Du"
              value={from}
              onChange={(newValue: any) => {
                setFrom(newValue)
              }}
              className="datePicker"
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    placeholder: "jj/mm/aaaa",
                  }}
                />
              )}
            />
            <DatePicker
              label="au"
              value={to}
              onChange={(newValue: any) => {
                setTo(newValue)
              }}
              className="datePicker"
              minDate={from}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    placeholder: "jj/mm/aaaa",
                  }}
                />
              )}
            />
          </div>
        </LocalizationProvider>
      </div>
      <div className="w-full flex justify-center">{content}</div>
      <TablePagination
        component="div"
        count={partInfosPaginate?.totalItems ?? 0}
        page={page - 1}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
        sx={{
          flexWrap: { xs: "wrap" },
          justifyContent: "center",
          pt: 4,
        }}
        className={css`
          .MuiTablePagination-toolbar {
            @media (max-width: 600px) {
              flex-wrap: wrap;
              justify-content: center;
            }
            align-items: baseline;
          }
        `}
      />
      <ValidateDialog
        show={showConfirmDelete}
        handleClose={() => {
          setPartdiagSelected(null)
          setShowConfirmDelete(false)
        }}
        modalTitle="Suppression définitive"
        handleConfirm={handleDelete}
        loading={isDeleteLoading}
      >
        Êtes-vous sûr de vouloir supprimer la demande de{" "}
        <span className="font-bold">{partdiagSelected?.email}</span> ? La
        suppression est définitive.
      </ValidateDialog>
    </StyledDiv>
  )
}

export default DiagRequestContent
